import BackendAuthHandler from 'BackendAuthHandler';
import BackendService from 'BackendService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FAQData } from '../../../components/faq/types';
import faqDataJson from '../../../data/faqData.json';
import {
  FAQ_API_PREFIX,
  buildApiImageUploadPlaceholder,
  convertImagesToPlaceholders,
  downloadJsonFile,
  mapAnswers,
  toPlainText,
} from './faqAdminUtils';

export const initialFAQData: FAQData = (faqDataJson as FAQData) || { groups: [] };

export function useFAQAdmin() {
  const [data, setData] = useState<FAQData>(initialFAQData);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const backendService = useMemo(() => new BackendService(), []);
  const [selectedGroup, setSelectedGroup] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<number>(0);
  const [imageHelperOpen, setImageHelperOpen] = useState<boolean>(false);
  const [imageHelperTab, setImageHelperTab] = useState<0 | 1>(0);
  const [assetAlt, setAssetAlt] = useState<string>('');
  const [publicAssetList, setPublicAssetList] = useState<string[] | null>(null);
  const [publicAssetFilter, setPublicAssetFilter] = useState<string>('');
  const [apiImages, setApiImages] = useState<{ relativePath: string; contentType: string }[]>([]);
  const [desiredFileName, setDesiredFileName] = useState<string>('');
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [insertAnswer, setInsertAnswer] = useState<((html: string) => void) | null>(null);
  const [faqLinkOpen, setFaqLinkOpen] = useState<boolean>(false);
  const [faqLinkGroup, setFaqLinkGroup] = useState<string>('');
  const [faqLinkQuestion, setFaqLinkQuestion] = useState<string>('');

  const refreshFromBucket = useCallback(
    async (showAlert: boolean = true) => {
      const parsed: FAQData = await backendService.downloadFAQFromBucket(FAQ_API_PREFIX);
      const placeholderData = mapAnswers(parsed, convertImagesToPlaceholders);
      setData(placeholderData);
      setSelectedGroup(0);
      setSelectedItem(0);
      setRefreshKey((k) => k + 1);
      if (showAlert) alert('Loaded FAQs from storage.');
    },
    [backendService],
  );

  useEffect(() => {
    const tryLoad = async () => {
      try {
        await backendService.requestImageAccess(['faq/*']);
      } catch (e) {
        console.warn('Image access request failed:', e);
      }
      try {
        const parsed: FAQData = await new BackendService().downloadFAQ();
        if (parsed?.groups) {
          const placeholderData = mapAnswers(parsed, convertImagesToPlaceholders);
          setData(placeholderData);
        } else {
          setData(initialFAQData);
        }
      } catch (e: unknown) {
        console.error(e);
        setData(initialFAQData);
      }
    };
    tryLoad();
  }, [backendService]);

  const currentGroup = data.groups[selectedGroup];
  const currentItem = currentGroup?.items[selectedItem];

  const addGroup = useCallback(() => {
    setData((prev) => {
      const groups = [...prev.groups, { group: 'New Group', items: [] }];
      setSelectedGroup(groups.length - 1);
      setSelectedItem(0);
      return { groups };
    });
  }, []);

  const removeGroup = useCallback((index: number) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups.splice(index, 1);
      return { groups };
    });
    setSelectedGroup(0);
    setSelectedItem(0);
  }, []);

  const moveGroup = useCallback((from: number, to: number) => {
    setData((prev) => {
      if (to < 0 || to >= prev.groups.length) return prev;
      const groups = [...prev.groups];
      const [moved] = groups.splice(from, 1);
      groups.splice(to, 0, moved);
      return { groups };
    });
    setSelectedGroup((current) => {
      if (current === from) return to;
      if (current === to) return from;
      if (from < to && current > from && current <= to) return current - 1;
      if (from > to && current >= to && current < from) return current + 1;
      return current;
    });
  }, []);

  const addItem = useCallback((groupIdx: number) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups[groupIdx].items.push({
        question: 'New question',
        answer: '<p>New answer</p>',
      });
      setSelectedGroup(groupIdx);
      setSelectedItem(groups[groupIdx].items.length - 1);
      return { groups };
    });
  }, []);

  const removeItem = useCallback((groupIdx: number, itemIdx: number) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups[groupIdx].items.splice(itemIdx, 1);
      return { groups };
    });
    setSelectedItem(0);
  }, []);

  const moveItem = useCallback(
    (groupIdx: number, from: number, to: number) => {
      setData((prev) => {
        const group = prev.groups[groupIdx];
        if (!group || to < 0 || to >= group.items.length) return prev;
        const groups = [...prev.groups];
        const items = [...groups[groupIdx].items];
        const [moved] = items.splice(from, 1);
        items.splice(to, 0, moved);
        groups[groupIdx] = { ...groups[groupIdx], items };
        return { groups };
      });
      setSelectedItem((current) => {
        if (selectedGroup !== groupIdx) return current;
        if (current === from) return to;
        if (from < to && current > from && current <= to) return current - 1;
        if (from > to && current >= to && current < from) return current + 1;
        return current;
      });
    },
    [selectedGroup],
  );

  const updateGroupName = useCallback((groupIdx: number, name: string) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups[groupIdx].group = name;
      return { groups };
    });
  }, []);

  const updateItemQuestion = useCallback((groupIdx: number, itemIdx: number, value: string) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups[groupIdx].items[itemIdx].question = toPlainText(value);
      return { groups };
    });
  }, []);

  const updateItemAnswer = useCallback((groupIdx: number, itemIdx: number, value: string) => {
    setData((prev) => {
      const groups = [...prev.groups];
      groups[groupIdx].items[itemIdx].answer = value;
      return { groups };
    });
  }, []);

  const saveToBucket = useCallback(async () => {
    try {
      await backendService.uploadFAQToBucket(FAQ_API_PREFIX, data);
      await refreshFromBucket(false);
      alert('FAQs uploaded and reloaded successfully');
    } catch (e: unknown) {
      console.error(e);
      const message = e instanceof Error ? e.message : String(e);
      alert(`Failed to upload FAQs JSON: ${message}`);
    }
  }, [backendService, data, refreshFromBucket]);

  const reloadFromBucket = useCallback(async () => {
    try {
      await refreshFromBucket(true);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alert(`Failed to load from storage: ${message}`);
      console.error(e);
    }
  }, [refreshFromBucket]);

  const revertToPublished = useCallback(() => {
    const placeholderData = mapAnswers(initialFAQData, convertImagesToPlaceholders);
    setData(placeholderData);
    setSelectedGroup(0);
    setSelectedItem(0);
    setRefreshKey((k) => k + 1);
  }, []);

  const downloadFromBucket = useCallback(async () => {
    try {
      const json = await backendService.downloadFAQFromBucket(FAQ_API_PREFIX);
      downloadJsonFile(json, 'faqs.json');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alert(`Failed to download from storage: ${message}`);
      console.error(e);
    }
  }, [backendService]);

  const clearFromBucket = useCallback(async () => {
    try {
      await backendService.clearFAQFromBucket(FAQ_API_PREFIX);
      alert('FAQs cleared from storage successfully');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      alert(`Failed to clear from storage: ${message}`);
      console.error(e);
    }
  }, [backendService]);

  const loadPublicAssetManifest = useCallback(async () => {
    try {
      const res = await fetch('/assets/faq/manifest.json');
      if (!res.ok) throw new Error('manifest missing');
      const files: string[] = await res.json();
      setPublicAssetList(files);
    } catch (_e) {
      setPublicAssetList(null);
    }
  }, []);

  const loadApiImages = useCallback(async () => {
    try {
      await new BackendAuthHandler().withAuthToken();
      const imgs = await new BackendService().listImages(FAQ_API_PREFIX);
      setApiImages(imgs);
    } catch (e) {
      console.error(e);
      setApiImages([]);
    }
  }, []);

  const handleUploadImage = useCallback(
    async (file: File) => {
      try {
        const ext = (desiredFileName || file.name).split('.').pop() || 'png';
        const targetName = desiredFileName || file.name;
        const fullRel = `${FAQ_API_PREFIX}/${targetName}`;
        const exists = apiImages.some((i) => i.relativePath === fullRel);
        if (exists) {
          const confirmReplace = window.confirm(`${fullRel} already exists. Replace it?`);
          if (!confirmReplace) return;
        }

        const baseName = targetName.replace(new RegExp(`\\.${ext}$`, 'i'), '');
        const token = await new BackendAuthHandler().withAuthToken();
        if (!token) throw new Error('Missing auth token for image upload');
        const { uploadUrl, relativePath: relative } =
          await new BackendService().generateArbitraryImageUploadUrl(
            `${FAQ_API_PREFIX}/${baseName}`,
            ext,
          );

        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!putRes.ok) {
          const t = await putRes.text().catch(() => '');
          throw new Error(`Upload failed: ${putRes.status} ${t}`);
        }

        const alt = assetAlt || targetName;
        const placeholder = buildApiImageUploadPlaceholder(relative, alt);
        insertAnswer?.(placeholder);
        setImageHelperOpen(false);
        setSelectedUploadFile(null);
        setDesiredFileName('');
        loadApiImages();
      } catch (e) {
        console.error(e);
        alert('Image upload failed');
      }
    },
    [apiImages, assetAlt, desiredFileName, insertAnswer, loadApiImages],
  );

  useEffect(() => {
    if (imageHelperOpen) {
      loadPublicAssetManifest();
      loadApiImages();
    }
  }, [imageHelperOpen, loadApiImages, loadPublicAssetManifest]);

  return {
    data,
    refreshKey,
    selectedGroup,
    selectedItem,
    setSelectedGroup,
    setSelectedItem,
    imageHelperOpen,
    setImageHelperOpen,
    imageHelperTab,
    setImageHelperTab,
    assetAlt,
    setAssetAlt,
    publicAssetList,
    publicAssetFilter,
    setPublicAssetFilter,
    apiImages,
    desiredFileName,
    setDesiredFileName,
    selectedUploadFile,
    setSelectedUploadFile,
    uploading,
    setUploading,
    insertAnswer,
    setInsertAnswer,
    faqLinkOpen,
    setFaqLinkOpen,
    faqLinkGroup,
    setFaqLinkGroup,
    faqLinkQuestion,
    setFaqLinkQuestion,
    currentGroup,
    currentItem,
    addGroup,
    removeGroup,
    moveGroup,
    addItem,
    removeItem,
    moveItem,
    updateGroupName,
    updateItemQuestion,
    updateItemAnswer,
    saveToBucket,
    reloadFromBucket,
    revertToPublished,
    downloadFromBucket,
    clearFromBucket,
    loadPublicAssetManifest,
    loadApiImages,
    handleUploadImage,
  };
}
